import type { CSSProperties, ReactNode } from 'react';
import { useTheme } from '../../theme';

export type AdminAnalyticsKpiCardVariant = 'blue' | 'purple' | 'green' | 'yellow';

export type AdminAnalyticsKpiTrendTone = 'positive' | 'negative' | 'muted';

export interface AdminAnalyticsKpiCardProps {
  variant: AdminAnalyticsKpiCardVariant;
  label: string;
  value: ReactNode;
  trend: ReactNode;
  trendTone?: AdminAnalyticsKpiTrendTone;
}

function TrendArrow({
  tone,
  size,
}: {
  tone: AdminAnalyticsKpiTrendTone;
  size: number;
}) {
  if (tone === 'muted') return null;
  const up = tone === 'positive';
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      aria-hidden
      style={{
        flexShrink: 0,
        transform: up ? undefined : 'rotate(180deg)',
      }}
    >
      <path
        d="M6 1.5L10.5 8.25H1.5L6 1.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function AdminAnalyticsKpiCard({
  variant,
  label,
  value,
  trend,
  trendTone = 'positive',
}: AdminAnalyticsKpiCardProps) {
  const theme = useTheme();
  const k = theme.palette.adminDashboard.analyticsKpi;
  const variantStyle = k[variant];

  const trendColor =
    trendTone === 'positive'
      ? k.trendPositive
      : trendTone === 'negative'
        ? k.trendNegative
        : k.trendMuted;

  const cardStyle: CSSProperties = {
    boxSizing: 'border-box',
    flex: '1 1 200px',
    minWidth: 0,
    maxWidth: '100%',
    background: variantStyle.gradient,
    border: `${theme.shape.borderWidth.thin} solid ${variantStyle.border}`,
    borderRadius: k.cardBorderRadius,
    padding: k.cardPadding,
    display: 'flex',
    flexDirection: 'column',
    gap: k.rowGap,
  };

  return (
    <div style={cardStyle}>
      <span
        style={{
          fontSize: theme.typography.fontSize.base,
          fontWeight: theme.typography.fontWeight.medium,
          color: theme.palette.text.secondary,
          lineHeight: theme.typography.lineHeight.normal,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: theme.typography.fontSize.analyticsKpiValue,
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.palette.text.primary,
          lineHeight: theme.typography.lineHeight.tight,
        }}
      >
        {value}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing.xs,
          fontSize: theme.typography.fontSize.xs,
          fontWeight: theme.typography.fontWeight.normal,
          color: trendColor,
          lineHeight: theme.typography.lineHeight.normal,
        }}
      >
        <TrendArrow tone={trendTone} size={k.trendArrowSize} />
        <span>{trend}</span>
      </div>
    </div>
  );
}
